import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon, CheckCircleIcon, ArrowUpTrayIcon, BanknotesIcon,
  ClockIcon, ExclamationTriangleIcon, CalendarDaysIcon,
  LockClosedIcon, XMarkIcon, ArrowDownTrayIcon, PlusCircleIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tm-root * { box-sizing:border-box; }
  .tm-root { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tmFade .4s ease both; }
  @keyframes tmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tm-back { display:inline-flex; align-items:center; gap:6px; font-size:.78rem; font-weight:600; color:#7880a4; cursor:pointer; margin-bottom:1.4rem; border:none; background:none; font-family:'Sora',sans-serif; padding:0; transition:color .15s; }
  .tm-back:hover { color:#0d9488; }
  .tm-back svg { width:14px; height:14px; stroke-width:2.5; }
  .tm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tm-title-wrap { display:flex; align-items:center; gap:12px; }
  .tm-avatar { width:46px; height:46px; border-radius:13px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; font-size:.95rem; font-weight:700; text-transform:uppercase; flex-shrink:0; }
  .tm-name  { font-size:1.25rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tm-month { font-size:.75rem; color:#9094a8; margin-top:2px; }
  .tm-actions { display:flex; gap:8px; flex-wrap:wrap; align-items:center; }
  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-teal  { background:#0d9488; color:white; }
  .btn-teal:hover  { background:#0b7b71; box-shadow:0 4px 14px rgba(13,148,136,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }
  .finalized-banner { display:flex; align-items:center; gap:10px; padding:12px 16px; border-radius:12px; margin-bottom:1.25rem; font-size:.8rem; font-weight:600; flex-wrap:wrap; }
  .finalized-banner.green { background:#ecfdf5; color:#065f46; border:1px solid #a7f3d0; }
  .finalized-banner.blue  { background:#eef2ff; color:#3730a3; border:1px solid #c7d2fe; }
  .finalized-banner svg   { width:16px; height:16px; stroke-width:2; flex-shrink:0; }
  .fb-meta { font-size:.7rem; font-weight:400; color:#6b7194; margin-top:1px; }
  .stat-grid { display:grid; grid-template-columns:repeat(5,1fr); gap:.9rem; margin-bottom:1.4rem; }
  .stat-card { background:white; border-radius:14px; padding:1rem 1.1rem; border:1px solid #e8ebf4; }
  .s-icon { width:32px; height:32px; border-radius:9px; display:flex; align-items:center; justify-content:center; margin-bottom:.7rem; }
  .s-icon svg { width:15px; height:15px; stroke-width:1.8; }
  .s-icon.blue{background:#eef2ff;color:#3b6ef8} .s-icon.green{background:#f0fdfa;color:#0d9488}
  .s-icon.amber{background:#fffbeb;color:#f59e0b} .s-icon.red{background:#fef2f2;color:#ef4444}
  .s-label { font-size:.68rem; font-weight:600; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; margin-bottom:2px; }
  .s-value { font-size:1.2rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; line-height:1; font-family:'JetBrains Mono',monospace; }
  .s-sub   { font-size:.68rem; color:#b0b5cc; margin-top:3px; }
  .summary-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; padding:18px 20px; margin-bottom:1.4rem; }
  .sp-title { font-size:.82rem; font-weight:700; color:#0f1123; margin-bottom:14px; }
  .sp-grid  { display:grid; grid-template-columns:1fr 1fr; gap:10px; }
  .sp-row   { display:flex; justify-content:space-between; align-items:center; padding:7px 0; border-bottom:1px solid #f3f5fb; font-size:.8rem; }
  .sp-row:last-child { border-bottom:none; }
  .sp-label { color:#6b7194; }
  .sp-val   { font-family:'JetBrains Mono',monospace; font-weight:600; color:#0f1123; }
  .sp-val.green { color:#0d9488; font-size:.9rem; }
  .sp-val.red   { color:#ef4444; }
  .table-panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.4rem; }
  .table-panel-header { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .tp-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .tp-sub   { font-size:.7rem; color:#9094a8; margin-top:1px; }
  .day-table { width:100%; border-collapse:collapse; }
  .day-table thead { background:#fafbff; border-bottom:1px solid #edf0f9; }
  .day-table th { padding:10px 14px; text-align:center !important; font-size:.65rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.07em; white-space:nowrap; }
  .day-table th.right { text-align:right; }
  .day-table tbody tr { border-bottom:1px solid #f3f5fb; transition:background .12s; }
  .day-table tbody tr:last-child { border-bottom:none; }
  .day-table tbody tr:hover { background:#fafbff; }
  .day-table td { padding:10px 14px; vertical-align:middle; font-size:.79rem; text-align:center !important; }
  .day-table td.right { text-align:right; }
  .day-date { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:500; color:#1a1d2e; }
  .day-dow  { font-size:.67rem; color:#9094a8; margin-top:1px; }
  .total-row td { background:#fafbff; font-weight:700; border-top:2px solid #e8ebf4 !important; text-align:center !important; }
  .day-cards { display:none; padding:10px; }
  .day-card  { background:#fafbff; border-radius:12px; border:1px solid #e8ebf4; padding:12px 13px; margin-bottom:8px; }
  .dc-top    { display:flex; align-items:center; justify-content:space-between; margin-bottom:8px; gap:8px; }
  .dc-date   { font-family:'JetBrains Mono',monospace; font-size:.8rem; font-weight:600; color:#0f1123; }
  .dc-dow    { font-size:.67rem; color:#9094a8; }
  .dc-row    { display:flex; align-items:center; justify-content:space-between; font-size:.75rem; padding:3px 0; border-bottom:1px solid #f0f2f8; gap:8px; }
  .dc-row:last-of-type { border-bottom:none; }
  .dc-label  { color:#9094a8; font-weight:600; white-space:nowrap; }
  .status-pill { display:inline-flex; align-items:center; font-size:.67rem; font-weight:600; padding:2px 9px; border-radius:99px; }
  .status-pill.present  { background:#ecfdf5; color:#10c98e; }
  .status-pill.absent   { background:#fef2f2; color:#ef4444; }
  .status-pill.on-leave { background:#eef2ff; color:#3b6ef8; }
  .status-pill.holiday  { background:#f3f0ff; color:#8b5cf6; }
  .mono-sm  { font-family:'JetBrains Mono',monospace; font-size:.75rem; color:#6b7194; }
  .amt       { font-family:'JetBrains Mono',monospace; font-size:.78rem; font-weight:600; color:#1a1d2e; }
  .amt.red   { color:#ef4444; }
  .amt.green { color:#0d9488; font-size:.82rem; }
  .amt.dim   { color:#c0c5dc; font-weight:400; }
  .tm-empty { text-align:center; padding:3.5rem 2rem; }
  .tm-empty-icon  { width:48px; height:48px; border-radius:13px; background:#f0fdfa; color:#0d9488; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .tm-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .tm-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .tm-empty-sub   { font-size:.76rem; color:#9094a8; }
  .tm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; font-size:.88rem; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#0d9488; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }
  .modal-backdrop { position:fixed; inset:0; background:rgba(15,17,35,.55); backdrop-filter:blur(4px); display:flex; align-items:center; justify-content:center; z-index:1000; padding:16px; }
  .modal-box  { background:white; border-radius:20px; width:100%; max-width:480px; max-height:90vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); animation:moIn .25s ease; }
  @keyframes moIn { from{opacity:0;transform:scale(.94)} to{opacity:1;transform:scale(1)} }
  .modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f2f8; }
  .modal-title { font-size:1rem; font-weight:700; color:#0f1123; }
  .modal-close { width:28px; height:28px; border-radius:7px; border:none; background:#f0f2f7; cursor:pointer; display:flex; align-items:center; justify-content:center; }
  .modal-close svg { width:14px; height:14px; stroke-width:2; }
  .modal-body  { padding:20px 22px; }
  .modal-footer{ display:flex; gap:8px; justify-content:flex-end; padding:14px 22px; border-top:1px solid #f0f2f8; flex-wrap:wrap; }
  .form-field  { display:flex; flex-direction:column; gap:5px; margin-bottom:12px; }
  .form-label  { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus { border-color:#0d9488; background:white; }
  .form-row    { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
  @media (max-width:1100px) { .stat-grid { grid-template-columns:repeat(3,1fr); } }
  @media (max-width:768px) {
    .tm-name { font-size:1.05rem; }
    .stat-grid { grid-template-columns:repeat(2,1fr); }
    .sp-grid { grid-template-columns:1fr; }
    .day-table-wrap { display:none !important; }
    .day-cards { display:block; }
    .modal-box { max-width:100%; border-radius:16px 16px 0 0; max-height:95vh; }
    .modal-backdrop { padding:0 10px; align-items:flex-end; }
    .form-row { grid-template-columns:1fr; }
    .tm-actions { width:100%; }
    .tm-actions .btn { flex:1; justify-content:center; }
  }
  @media (max-width:480px) { .stat-grid { grid-template-columns:1fr 1fr; } }
`;

const initials  = (n='') => n.split(' ').slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?';
const fmt       = (n) => `Rs. ${(n||0).toLocaleString('en-PK',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
const fmtTime   = (d) => d ? new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit',hour12:true}) : '—';
const fmtDate   = (d) => new Date(d).toLocaleDateString('en-US',{day:'numeric',month:'short'});
const dayOfWeek = (d) => new Date(d).toLocaleDateString('en-US',{weekday:'short'});
const monthLabel= (m) => { const[y,mn]=m.split('-').map(Number); return new Date(y,mn-1,1).toLocaleDateString('en-US',{month:'long',year:'numeric'}); };

export default function OwnerAdminSalaryMonth() {
  const { schoolId, teacherId, month } = useParams();
  const navigate = useNavigate();

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [finalizeForm, setFinalizeForm] = useState({ bonuses:'0', allowances:'0', otherDeductions:'0', notes:'' });
  const [saving,  setSaving]  = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' });
  const [adding,  setAdding]  = useState(false);

  useEffect(() => { fetchDetail(); }, [teacherId, month, schoolId]);

  const fetchDetail = async () => {
    try {
      setLoading(true);
      const { data: res } = await api.get(`/owner/branches/${schoolId}/admin-salary/preview/${month}?adminId=${teacherId}`);
      setData(res);
    } catch { toast.error('Failed to load salary detail'); }
    finally { setLoading(false); }
  };

  // Build display values — backend returns flat fields + totals object
  const teacher     = data?.admin || data?.teacher;
  const days        = data?.days || [];
  // Prefer data.totals if present, otherwise use flat fields
  const totals = data?.totals || {
    monthlySalary:    data?.monthlySalary    || 0,
    workingDays:      data?.workingDays      || 0,
    daysPresent:      data?.daysPresent      || 0,
    daysAbsent:       data?.daysAbsent       || 0,
    daysOnLeave:      data?.daysOnLeave      || 0,
    totalLateMinutes: data?.totalLateMinutes || 0,
    absentDeduction:  data?.absentDeduction  || 0,
    lateDeduction:    data?.lateDeduction    || data?.lateDeduct || 0,
    totalDeductions:  data?.totalDeductions  || 0,
    netSalary:        data?.netSalary        || 0,
  };
  const savedRecord = data?.savedRecord;
  const isFinalized = !!savedRecord;

  const handleExportCSV = () => {
    if (!days.length) return toast.error('No records to export');
    const headers = ['Date','Day','Status','Check In','Check Out','Late Min','Net'];
    const rows = days.map(d => [
      fmtDate(d.date), dayOfWeek(d.date), d.status,
      fmtTime(d.checkIn), fmtTime(d.checkOut),
      d.lateMinutes || 0,
      (d.netDay || 0).toFixed(2),
    ]);
    const csv  = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type:'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url;
    a.download = `salary-${teacher?.name?.replace(/\s+/g,'-') || 'admin'}-${month}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleAddDay = async () => {
    if (!addForm.date) return toast.error('Please select a date');
    setAdding(true);
    try {
      await api.post(`/owner/branches/${schoolId}/admin-attendance/mark`, {
        adminId:          teacherId,
        date:             addForm.date,
        status:           addForm.status,
        checkIn:          addForm.checkIn  ? `${addForm.date}T${addForm.checkIn}:00`  : undefined,
        checkOut:         addForm.checkOut ? `${addForm.date}T${addForm.checkOut}:00` : undefined,
        lateMinutes:      Number(addForm.lateMinutes)      || 0,
        earlyExitMinutes: Number(addForm.earlyExitMinutes) || 0,
      });
      toast.success('Attendance added');
      setAddOpen(false);
      setAddForm({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' });
      fetchDetail();
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to add attendance'); }
    finally { setAdding(false); }
  };

  const handleFinalize = async () => {
    setSaving(true);
    try {
      await api.post(`/owner/branches/${schoolId}/admin-salary/finalize`, {
        adminId:         teacherId,
        month,
        bonuses:         Number(finalizeForm.bonuses)         || 0,
        allowances:      Number(finalizeForm.allowances)      || 0,
        otherDeductions: Number(finalizeForm.otherDeductions) || 0,
        notes:           finalizeForm.notes,
      });
      toast.success('Salary finalized ✓');
      setFinalizeOpen(false);
      fetchDetail();
    } catch { toast.error('Failed to finalize'); }
    finally { setSaving(false); }
  };

  const handleMarkPaid = async () => {
    if (!savedRecord?._id) return;
    setSaving(true);
    try {
      await api.put(`/owner/salary/record/${savedRecord._id}/status`, { status: 'paid' });
      toast.success('Marked as paid ✓');
      fetchDetail();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const handleMarkUnpaid = async () => {
    if (!savedRecord?._id) return;
    setSaving(true);
    try {
      await api.put(`/owner/salary/record/${savedRecord._id}/status`, { status: 'unpaid' });
      toast.success('Marked as unpaid');
      fetchDetail();
    } catch { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="tm-root"><style>{style}</style><div className="tm-loading"><div className="spinner"/>Loading…</div></div>;
  if (!data)   return null;

  return (
    <div className="tm-root"><style>{style}</style>

      <button className="tm-back" onClick={() => navigate(`/owner/admin-salary/${schoolId}/${teacherId}`)}>
        <ChevronLeftIcon />Back to Months
      </button>

      <div className="tm-header">
        <div className="tm-title-wrap">
          <div className="tm-avatar">{initials(teacher?.name)}</div>
          <div>
            <div className="tm-name">{teacher?.name}</div>
            <div className="tm-month">{monthLabel(month)}</div>
          </div>
        </div>
        <div className="tm-actions">
          <button className="btn btn-ghost" onClick={handleExportCSV}><ArrowDownTrayIcon />Export CSV</button>
          <button className="btn btn-ghost" onClick={() => { setAddOpen(true); setAddForm({ date:'', checkIn:'', checkOut:'', lateMinutes:'0', earlyExitMinutes:'0', status:'present' }); }}><PlusCircleIcon />Add Day</button>
          {isFinalized ? (<>
            {savedRecord.status === 'unpaid'
              ? <button className="btn btn-green" onClick={handleMarkPaid} disabled={saving}><CheckCircleIcon />Mark Paid</button>
              : <button className="btn btn-ghost" onClick={handleMarkUnpaid} disabled={saving} style={{color:'#ef4444',border:'1px solid #fecaca'}}>Mark Unpaid</button>}
            <button className="btn btn-teal" onClick={() => { setFinalizeForm({ bonuses:'0', allowances:'0', otherDeductions:'0', notes:'' }); setFinalizeOpen(true); }}><ArrowUpTrayIcon />Re-finalize</button>
          </>) : (
            <button className="btn btn-teal" onClick={() => { setFinalizeForm({ bonuses:'0', allowances:'0', otherDeductions:'0', notes:'' }); setFinalizeOpen(true); }}><LockClosedIcon />Finalize Salary</button>
          )}
        </div>
      </div>

      {isFinalized && (
        <div className={`finalized-banner ${savedRecord.status==='paid'?'blue':'green'}`}>
          {savedRecord.status==='paid' ? <CheckCircleIcon/> : <LockClosedIcon/>}
          <div>
            <div>{savedRecord.status==='paid' ? `Paid on ${fmtDate(savedRecord.paidAt)} by ${savedRecord.paidBy||'Owner'}` : 'Finalized — awaiting payment'}</div>
            <div className="fb-meta">Finalized on {fmtDate(savedRecord.finalizedAt)} by {savedRecord.finalizedBy||'Owner'}</div>
          </div>
        </div>
      )}

      {/* Stat cards */}
      <div className="stat-grid">
        <div className="stat-card"><div className="s-icon blue"><CalendarDaysIcon/></div><div className="s-label">Present</div><div className="s-value">{totals.daysPresent}</div><div className="s-sub">{totals.daysAbsent} absent</div></div>
        <div className="stat-card"><div className="s-icon green"><BanknotesIcon/></div><div className="s-label">Monthly Salary</div><div className="s-value" style={{fontSize:'.82rem'}}>{fmt(totals.monthlySalary)}</div><div className="s-sub">{totals.workingDays} working days</div></div>
        <div className="stat-card"><div className="s-icon amber"><ClockIcon/></div><div className="s-label">Late Minutes</div><div className="s-value">{totals.totalLateMinutes}</div><div className="s-sub">this month</div></div>
        <div className="stat-card"><div className="s-icon red"><ExclamationTriangleIcon/></div><div className="s-label">Deductions</div><div className="s-value" style={{fontSize:'.82rem'}}>{fmt(totals.totalDeductions)}</div><div className="s-sub">total</div></div>
        <div className="stat-card"><div className="s-icon green"><BanknotesIcon/></div><div className="s-label">Net Salary</div><div className="s-value" style={{fontSize:'.82rem',color:'#0d9488'}}>{fmt(totals.netSalary)}</div><div className="s-sub">this month</div></div>
      </div>

      {/* Summary breakdown */}
      <div className="summary-panel">
        <div className="sp-title">Salary Breakdown</div>
        <div className="sp-grid">
          <div>
            <div className="sp-row"><span className="sp-label">Monthly Salary</span><span className="sp-val">{fmt(totals.monthlySalary)}</span></div>
            <div className="sp-row"><span className="sp-label">Working Days</span><span className="sp-val">{totals.workingDays}</span></div>
            <div className="sp-row"><span className="sp-label">Days Present</span><span className="sp-val">{totals.daysPresent}</span></div>
            <div className="sp-row"><span className="sp-label">Days Absent</span><span className="sp-val">{totals.daysAbsent}</span></div>
          </div>
          <div>
            <div className="sp-row"><span className="sp-label">Absent Deduction</span><span className="sp-val red">{fmt(totals.absentDeduction)}</span></div>
            <div className="sp-row"><span className="sp-label">Late Deduction</span><span className="sp-val red">{fmt(totals.lateDeduction)}</span></div>
            <div className="sp-row"><span className="sp-label">Total Deductions</span><span className="sp-val red">{fmt(totals.totalDeductions)}</span></div>
            <div className="sp-row"><span className="sp-label">Net Salary</span><span className="sp-val green">{fmt(totals.netSalary)}</span></div>
          </div>
        </div>
      </div>

      {/* Day table */}
      <div className="table-panel">
        <div className="table-panel-header">
          <div><div className="tp-title">Day-by-Day Attendance</div><div className="tp-sub">{days.length} records</div></div>
        </div>
        {!days.length ? (
          <div className="tm-empty"><div className="tm-empty-icon"><CalendarDaysIcon/></div><div className="tm-empty-title">No attendance records</div><div className="tm-empty-sub">No check-ins for this month.</div></div>
        ) : (<>
          {/* Desktop */}
          <div className="day-table-wrap" style={{overflowX:'auto'}}>
            <table className="day-table">
              <thead><tr>
                <th>Date</th><th>Status</th><th>Check In</th><th>Check Out</th><th>Late Min</th><th className="right">Net</th>
              </tr></thead>
              <tbody>
                {days.map((d, i) => (
                  <tr key={i}>
                    <td><div className="day-date">{fmtDate(d.date)}</div><div className="day-dow">{dayOfWeek(d.date)}</div></td>
                    <td><span className={`status-pill ${d.status||'absent'}`}>{d.status||'absent'}</span></td>
                    <td><span className="mono-sm">{fmtTime(d.checkIn)}</span></td>
                    <td><span className="mono-sm">{fmtTime(d.checkOut)}</span></td>
                    <td><span className="mono-sm" style={{color:d.lateMinutes>0?'#f59e0b':'#b0b5cc'}}>{d.lateMinutes>0?`${d.lateMinutes}m`:'—'}</span></td>
                    <td className="right"><span className="amt green">{fmt(d.netDay||0)}</span></td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td colSpan={4}><span style={{fontFamily:'Sora',fontWeight:700,fontSize:'.8rem'}}>Monthly Total</span><div style={{fontSize:'.67rem',color:'#9094a8',marginTop:2}}>{totals.daysPresent} present · {totals.daysAbsent} absent</div></td>
                  <td><span className="mono-sm" style={{color:'#f59e0b',fontWeight:700}}>{totals.totalLateMinutes>0?`${totals.totalLateMinutes}m`:'—'}</span></td>
                  <td className="right"><span className="amt green" style={{fontWeight:700,fontSize:'.9rem'}}>{fmt(totals.netSalary)}</span></td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="day-cards">
            {days.map((d, i) => (
              <div key={i} className="day-card">
                <div className="dc-top">
                  <div><div className="dc-date">{fmtDate(d.date)}</div><div className="dc-dow">{dayOfWeek(d.date)}</div></div>
                  <span className={`status-pill ${d.status||'absent'}`}>{d.status||'absent'}</span>
                </div>
                {d.checkIn && <div className="dc-row"><span className="dc-label">In / Out</span><span className="mono-sm">{fmtTime(d.checkIn)} → {fmtTime(d.checkOut)}</span></div>}
                {d.lateMinutes>0 && <div className="dc-row"><span className="dc-label">Late</span><span style={{color:'#f59e0b',fontFamily:"'JetBrains Mono',monospace",fontSize:'.75rem',fontWeight:600}}>{d.lateMinutes}m</span></div>}
                <div className="dc-row"><span className="dc-label">Net</span><span className="amt green">{fmt(d.netDay||0)}</span></div>
              </div>
            ))}
          </div>
        </>)}
      </div>

      {/* Add Day Modal */}
      {addOpen && createPortal(
        <div className="modal-backdrop" onClick={() => setAddOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">Add Attendance Record</div>
              <button className="modal-close" onClick={() => setAddOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Date</label>
                  <input type="date" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.date} min={`${month}-01`} max={`${month}-31`}
                    onChange={e => setAddForm(p => ({...p, date: e.target.value}))} />
                </div>
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Status</label>
                  <select className="form-select" value={addForm.status} onChange={e => setAddForm(p => ({...p, status: e.target.value}))}>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="on-leave">On Leave</option>
                    <option value="holiday">Holiday</option>
                  </select>
                </div>
                <div className="form-field">
                  <label className="form-label">Check In</label>
                  <input type="time" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.checkIn} onChange={e => setAddForm(p => ({...p, checkIn: e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Check Out</label>
                  <input type="time" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.checkOut} onChange={e => setAddForm(p => ({...p, checkOut: e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Late Minutes</label>
                  <input type="number" min="0" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.lateMinutes} onChange={e => setAddForm(p => ({...p, lateMinutes: e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Early Exit Min</label>
                  <input type="number" min="0" className="form-input" style={{fontFamily:"'JetBrains Mono',monospace"}}
                    value={addForm.earlyExitMinutes} onChange={e => setAddForm(p => ({...p, earlyExitMinutes: e.target.value}))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setAddOpen(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={handleAddDay} disabled={adding}>{adding?'Adding…':'Add Record'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

      {/* Finalize Modal */}
      {finalizeOpen && createPortal(
        <div className="modal-backdrop" onClick={() => setFinalizeOpen(false)}>
          <div className="modal-box" onClick={e => e.stopPropagation()}>
            <div className="modal-head">
              <div className="modal-title">{isFinalized ? 'Re-finalize Salary' : 'Finalize Salary'}</div>
              <button className="modal-close" onClick={() => setFinalizeOpen(false)}><XMarkIcon /></button>
            </div>
            <div className="modal-body">
              <div style={{background:'#f8f9fc',borderRadius:10,padding:'10px 14px',marginBottom:14,fontSize:'.82rem'}}>
                <strong>{teacher?.name}</strong> — {monthLabel(month)}
              </div>
              <div style={{background:'#ecfdf5',border:'1px solid #a7f3d0',borderRadius:9,padding:'8px 12px',fontSize:'.74rem',color:'#065f46',marginBottom:14}}>
                Computed net: <strong>{fmt(totals.netSalary)}</strong> · Present: {totals.daysPresent} · Absent: {totals.daysAbsent}
              </div>
              <div className="form-row">
                <div className="form-field">
                  <label className="form-label">Bonuses (Rs.)</label>
                  <input type="number" min="0" className="form-input" value={finalizeForm.bonuses} onChange={e => setFinalizeForm(p => ({...p, bonuses: e.target.value}))} />
                </div>
                <div className="form-field">
                  <label className="form-label">Allowances (Rs.)</label>
                  <input type="number" min="0" className="form-input" value={finalizeForm.allowances} onChange={e => setFinalizeForm(p => ({...p, allowances: e.target.value}))} />
                </div>
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Other Deductions (Rs.)</label>
                  <input type="number" min="0" className="form-input" value={finalizeForm.otherDeductions} onChange={e => setFinalizeForm(p => ({...p, otherDeductions: e.target.value}))} />
                </div>
                <div className="form-field" style={{gridColumn:'1/-1'}}>
                  <label className="form-label">Notes</label>
                  <input type="text" className="form-input" placeholder="Optional note…" value={finalizeForm.notes} onChange={e => setFinalizeForm(p => ({...p, notes: e.target.value}))} />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setFinalizeOpen(false)}>Cancel</button>
              <button className="btn btn-teal" onClick={handleFinalize} disabled={saving}>{saving?'Saving…':'Confirm Finalize'}</button>
            </div>
          </div>
        </div>
      
      , document.body)}

    </div>
  );
}